import { nanoid } from "nanoid";

import { getLogger } from "Services/LoggingService";

const STORAGE_KEY_PREFIX = "regal-copilot-widget-prompt";
const PROMPT_TTL_MS = 60_000;
const VALID_LAUNCH_ID = /^[A-Za-z0-9_-]{10,64}$/;

const logger = getLogger("copilotPromptHandoff", { regal_copilot: true });

type StoredCopilotPrompt = {
    prompt: string;
    expiresAt: number;
};

function getStorageKey(launchId: string): string {
    return `${STORAGE_KEY_PREFIX}-${launchId}`;
}

function removeExpiredCopilotWidgetPrompts(now: number): void {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
        const storageKey = localStorage.key(index);
        if (!storageKey?.startsWith(getStorageKey(""))) {
            continue;
        }

        const serializedPrompt = localStorage.getItem(storageKey);
        if (!serializedPrompt) {
            continue;
        }

        try {
            const storedPrompt: unknown = JSON.parse(serializedPrompt);
            if (isStoredCopilotPrompt(storedPrompt) && storedPrompt.expiresAt <= now) {
                localStorage.removeItem(storageKey);
            }
        } catch (error) {
            logger.error("Failed to parse stored Copilot widget prompt", { error, storageKey });
            localStorage.removeItem(storageKey);
        }
    }
}

function isStoredCopilotPrompt(value: unknown): value is StoredCopilotPrompt {
    if (!value || typeof value !== "object") {
        return false;
    }

    const storedPrompt = value as Record<string, unknown>;
    return (
        typeof storedPrompt.prompt === "string" &&
        typeof storedPrompt.expiresAt === "number" &&
        Number.isFinite(storedPrompt.expiresAt)
    );
}

export function createCopilotPromptLaunchId(): string {
    return nanoid();
}

export function isValidCopilotPromptLaunchId(launchId: string): boolean {
    return VALID_LAUNCH_ID.test(launchId);
}

export function storeCopilotWidgetPrompt(prompt: string, launchId: string): boolean {
    if (!isValidCopilotPromptLaunchId(launchId)) {
        throw new Error("Invalid Copilot prompt launch ID");
    }
    if (!prompt.trim()) {
        throw new Error("Copilot widget prompt is empty");
    }

    const now = Date.now();
    const storedPrompt: StoredCopilotPrompt = {
        prompt,
        expiresAt: now + PROMPT_TTL_MS,
    };

    removeExpiredCopilotWidgetPrompts(now);
    try {
        localStorage.setItem(getStorageKey(launchId), JSON.stringify(storedPrompt));
        return true;
    } catch (error) {
        logger.error("Failed to store Copilot widget prompt", { error, launchId });
        return false;
    }
}

export function takeCopilotWidgetPrompt(launchId: string): string {
    if (!isValidCopilotPromptLaunchId(launchId)) {
        throw new Error("Invalid Copilot prompt launch ID");
    }

    const storageKey = getStorageKey(launchId);
    const serializedPrompt = localStorage.getItem(storageKey);
    localStorage.removeItem(storageKey);

    if (!serializedPrompt) {
        throw new Error("Copilot widget prompt was not found");
    }

    let storedPrompt: unknown;
    try {
        storedPrompt = JSON.parse(serializedPrompt);
    } catch {
        throw new Error("Copilot widget prompt is invalid");
    }

    if (!isStoredCopilotPrompt(storedPrompt) || !storedPrompt.prompt.trim()) {
        throw new Error("Copilot widget prompt is invalid");
    }
    if (storedPrompt.expiresAt <= Date.now()) {
        throw new Error("Copilot widget prompt has expired");
    }

    return storedPrompt.prompt;
}
